var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/pro-regular-svg-icons/index";
import { consumerTheme } from "syf-component-library/defaultTheme";
import { formatDateObjectToString, formatDisplayDate } from "syf-js-utilities";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";
import calcDaysLeft from "../../../helpers/calcDaysLeft";
import TooltipButton from "../../../ui/atoms/TooltipButton";
import diffBetwDateInMonths from "../../../helpers/diffBetwDateInMonths";
import Step from "../../../ui/atoms/Step";
import { ExclamationWrapper } from "../../../ui/molecules/DataSections/subcomponents";
import isLoanClosed from "../../../helpers/isLoanClosed";
var calculateDaysExhausted = function (_a) {
    var startDate = _a.startDate, endDate = _a.endDate;
    var startDateEndDateDiff = diffBetwDateInMonths(startDate, endDate) || 1;
    var daysOnLoanStepperExhausted = (diffBetwDateInMonths(startDate, formatDateObjectToString(new Date())) *
        100) /
        startDateEndDateDiff;
    return daysOnLoanStepperExhausted > 100 ? 100 : daysOnLoanStepperExhausted;
};
var getWaivedInterestDataSections = function (accountSummaryData, autopayStatusData, t, setIsWaivedInterestTooltip) {
    var billing = accountSummaryData.billing, installment = accountSummaryData.installment, conditions = accountSummaryData.conditions;
    var nextPaymentDate = billing.nextPaymentDate, totalMinimumPaymentDue = billing.totalMinimumPaymentDue, waivedInterest = billing.waivedInterest, lastPaymentDate = billing.lastPaymentDate, lastPaymentAmount = billing.lastPaymentAmount;
    var _a = installment || {}, loanTerm = _a.loanTerm, interestRate = _a.interestRate, startDate = _a.startDate, endDate = _a.endDate;
    var _b = waivedInterest || {}, promoEndDate = _b.promoEndDate, payOffBalance = _b.payOffBalance;
    var autopayValue = (autopayStatusData === null || autopayStatusData === void 0 ? void 0 : autopayStatusData.enrollment)
        ? "grid.labels.autoPayStatus.on"
        : "grid.labels.autoPayStatus.off";
    var isActive = promoEndDate && new Date(promoEndDate) > new Date();
    var stepperAdditionalText = isActive
        ? t("waivedInterest.promoEndDate", {
            promoEndDate: formatDisplayDate(promoEndDate),
        })
        : t("waivedInterest.expiredPromoEndDate", {
            promoEndDate: promoEndDate && formatDisplayDate(promoEndDate),
        });
    var daysExhausted = calculateDaysExhausted({ startDate: startDate, endDate: endDate });
    var loanStartDateStep = (_jsx(Step, { label: t("grid.labels.loanStartDate"), value: startDate && formatDisplayDate(startDate) }));
    var promoEndDateStep = (_jsx(Step, { labelWeight: "bold", label: t("grid.labels.promotionalPeriodEnds"), value: promoEndDate && formatDisplayDate(promoEndDate) }));
    var loanEndDateStep = (_jsx(Step, { label: t("grid.labels.loanEndDate"), value: endDate && formatDisplayDate(endDate) }));
    var isClosed = isLoanClosed(conditions);
    var showUnavailableText = isClosed;
    var unavailableText = t("error.uiComponent");
    return {
        dataSections: [
            {
                heading: t("grid.titles.loanSummary"),
                sectionName: "loan-summary",
                dataElements: [
                    {
                        value: startDate,
                        label: t("grid.labels.loanStartDate"),
                        type: "date",
                    },
                    {
                        value: loanTerm,
                        label: t("grid.labels.loanTerm"),
                        type: "months",
                    },
                    {
                        value: interestRate,
                        label: t("grid.labels.interestRate"),
                        type: "percentage",
                    },
                ],
            },
            {
                heading: t("grid.titles.payments"),
                sectionName: "payments",
                dataElements: [
                    {
                        value: showUnavailableText ? unavailableText : nextPaymentDate,
                        type: showUnavailableText ? "text" : "date",
                        label: t("grid.labels.nextPaymentDate"),
                    },
                    {
                        value: showUnavailableText
                            ? unavailableText
                            : totalMinimumPaymentDue,
                        label: t("grid.labels.nextAmountDue"),
                        type: showUnavailableText ? "text" : "amount",
                        toolTipText: t("grid.labels.nextAmountMothlyTooltip"),
                    },
                    {
                        value: lastPaymentDate,
                        label: t("grid.labels.lastPaymentDate"),
                        type: "date",
                    },
                    {
                        value: lastPaymentAmount,
                        label: t("grid.labels.lastPaymentAmount"),
                        type: "amount",
                    },
                    {
                        value: t(autopayValue),
                        label: t("grid.labels.autoPayStatus.text"),
                        type: "text",
                    },
                ],
            },
            {
                heading: t("grid.titles.loanInformation"),
                sectionName: "loan-information",
                hasStepper: true,
                headerToolTipText: (_jsx(TooltipButton, __assign({ type: "button", onClick: function () {
                        setIsWaivedInterestTooltip(true);
                    } }, { children: _jsx(FontAwesomeIcon, { icon: faQuestionCircle, size: "lg", color: consumerTheme.icon }) }))),
                dataElements: __spreadArray(__spreadArray([
                    {
                        value: promoEndDate,
                        label: t("grid.labels.promoExpirationDate"),
                        type: "date",
                        additionalValue: calcDaysLeft(t("waivedInterest.days"), promoEndDate),
                    },
                    {
                        value: endDate,
                        label: t("grid.labels.loanEndDate"),
                        type: "date",
                    }
                ], (promoEndDate && endDate && startDate
                    ? [
                        {
                            progressPercentage: daysExhausted,
                            startStep: {
                                stepData: loanStartDateStep,
                            },
                            endStep: {
                                stepData: loanEndDateStep,
                                className: "endStepBackGroundColor",
                            },
                            milestoneStep: {
                                stepData: promoEndDateStep,
                                value: 50,
                                stepIcon: (_jsx(ExclamationWrapper, { children: _jsx(FontAwesomeIcon, { icon: faExclamationTriangle }) })),
                            },
                            tooltipText: t("grid.labels.promotionalPeriod"),
                            additionalText: stepperAdditionalText,
                            dataCy: "waived-interest-loan-information",
                        },
                    ]
                    : []), true), [
                    {
                        value: isActive ? payOffBalance : "0",
                        label: t("grid.labels.promotionalBalance"),
                        type: "amount",
                    },
                    {
                        value: isActive
                            ? t("grid.labels.promotionStatus.active")
                            : t("grid.labels.promotionStatus.expired"),
                        label: t("grid.labels.promotionStatus.text"),
                        type: "text",
                        toolTipText: isActive
                            ? t("grid.promotionStatusTooltip.active")
                            : t("grid.promotionStatusTooltip.expired"),
                    },
                ], false),
            },
        ],
    };
};
export default getWaivedInterestDataSections;
//# sourceMappingURL=getWaivedInterestDataSections.js.map